import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { Col, Row, Spin, Table, notification } from "antd";

import ButtonStandard from "../../utils/button";
import TagStatus from "../../utils/tagStatus";
import { BatchCard, Body, Container, Footer, HeaderFields } from "./styles";
import { numberMask } from "../../../services/helpersMethodsService";
import {
  indexProductionBatches,
  showProductionOrder,
} from "../../../services/productionOrderService";
import { processReportProductionOrderSheet } from "../../../services/reportService";
import { resolveProductionOrderBlobError } from "../../../utils/productionOrderErrors";

const STATUS_COLORS = {
  Calculated: {
    background: "#FFDBBC",
    borderColor: "#FE8D2A",
    color: "#FE8D2A",
  },
  Scheduled: {
    background: "#E4F1FF",
    borderColor: "#4A85AE",
    color: "#4A85AE",
  },
  InProgress: {
    background: "#F6FFC8",
    borderColor: "#A9C133",
    color: "#A9C133",
  },
  ClosedTotal: {
    background: "#C5F1CA",
    borderColor: "#106518",
    color: "#106518",
  },
  ClosedPartial: {
    background: "#FFDBBC",
    borderColor: "#FE8D2A",
    color: "#FE8D2A",
  },
  Cancelled: {
    background: "#FBC7C7",
    borderColor: "#D44C4C",
    color: "#D44C4C",
  },
};

const byName = (a, b) =>
  String(a.name ?? "").localeCompare(String(b.name ?? ""));

const bySequence = (a, b) => {
  const left = a.inclusionOrder;
  const right = b.inclusionOrder;

  if (left == null && right == null) {
    return byName(a, b);
  }
  if (left == null) {
    return 1;
  }
  if (right == null) {
    return -1;
  }
  if (left !== right) {
    return left - right;
  }

  return byName(a, b);
};

const DrawerProductionOrderDetails = ({ visible, orderId, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [batches, setBatches] = useState([]);

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const fetchData = useCallback(async () => {
    if (orderId == null) {
      return;
    }

    setIsLoading(true);
    try {
      const [orderResponse, batchesResponse] = await Promise.all([
        showProductionOrder({ groupId, farmId, id: orderId }),
        indexProductionBatches({ groupId, farmId, id: orderId }),
      ]);
      setOrder(orderResponse?.data?.results ?? null);
      setBatches(batchesResponse?.data?.results ?? []);
    } catch (error) {
      console.error(error);
      notification.error({
        message: translation.production.messages.errorLoad,
      });
    } finally {
      setIsLoading(false);
    }
  }, [groupId, farmId, orderId, translation]);

  useEffect(() => {
    if (visible) {
      fetchData();
    } else {
      setOrder(null);
      setBatches([]);
    }
  }, [visible, fetchData]);

  const handlePrint = useCallback(async () => {
    if (orderId == null) {
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await processReportProductionOrderSheet({
        groupId,
        farmId,
        body: { productionOrderIds: [orderId] },
      });

      const downloadUrl = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute(
        "download",
        `Ficha_de_Producao_${moment().format("YYYYMMDD_HHmm")}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      notification.error({
        message: await resolveProductionOrderBlobError(
          error,
          translation,
          translation.production.messages.errorPrint,
        ),
      });
    } finally {
      setIsLoading(false);
    }
  }, [groupId, farmId, orderId, translation]);

  const t = translation.production.details;

  const numberOfBatches =
    order?.numberOfBatches != null && order.numberOfBatches > 0
      ? order.numberOfBatches
      : 0;

  const plannedPerBatchOf = useCallback(
    (item) =>
      item.plannedOriginalMatterKg != null && numberOfBatches > 0
        ? item.plannedOriginalMatterKg / numberOfBatches
        : 0,
    [numberOfBatches],
  );

  const buildRows = useCallback(
    (executedBatch) => {
      if (executedBatch != null) {
        return (executedBatch.ingredients ?? [])
          .map((ingredient) => ({
            key: ingredient.id ?? ingredient.supplementId,
            name: ingredient.supplementName,
            inclusionOrder: ingredient.inclusionOrder,
            batchNumber: ingredient.batchNumber,
            mixingTime: ingredient.mixingTime,
            planned: ingredient.quantityPlanned,
            realized: ingredient.quantityRealized,
          }))
          .sort(bySequence);
      }

      return (order?.items ?? [])
        .map((item) => ({
          key: item.supplementId,
          name: item.supplementName,
          inclusionOrder: item.inclusionOrder,
          batchNumber: item.batchNumber,
          mixingTime: item.mixingTime,
          planned: plannedPerBatchOf(item),
          realized: null,
        }))
        .sort(bySequence);
    },
    [order, plannedPerBatchOf],
  );

  const field = (label, value) => (
    <Col xs={12} sm={12} md={8} lg={8} xl={8}>
      <span className="label">{label}</span>
      <span className="value">{value}</span>
    </Col>
  );

  const batchCards = () => {
    const cards = [];

    for (let number = 1; number <= numberOfBatches; number += 1) {
      const executedBatch = batches.find(
        (batch) => batch.batchNumber === number,
      );
      const isExecuted = executedBatch != null;

      cards.push(
        <BatchCard key={number}>
          <Row type="flex" align="middle" gutter={8} className="batchHeader">
            <Col>
              <strong>
                {t.batchLabel} #{number}
              </strong>
            </Col>
            <Col>
              <TagStatus
                background={isExecuted ? "#C5F1CA" : "#EFEFEF"}
                borderColor={isExecuted ? "#106518" : "#9074bf"}
                color={isExecuted ? "#106518" : "#9074bf"}
              >
                {isExecuted ? t.executed : t.planned}
              </TagStatus>
            </Col>
            {isExecuted && executedBatch.manufactureDatetime && (
              <Col className="batchDate">
                {moment(executedBatch.manufactureDatetime).format("DD/MM/YYYY")}
              </Col>
            )}
          </Row>
          <Table
            rowKey="key"
            size="small"
            pagination={false}
            dataSource={buildRows(executedBatch)}
          >
            <Table.Column
              title={t.columns.sequence}
              dataIndex="inclusionOrder"
              align="center"
              width={60}
              render={(value) =>
                value == null ? <span className="empty">—</span> : value
              }
            />
            <Table.Column
              title={t.columns.ingredient}
              dataIndex="name"
              align="left"
            />
            <Table.Column
              title={t.columns.batchNumber}
              dataIndex="batchNumber"
              align="left"
              render={(value) =>
                value == null || value === "" ? (
                  <span className="empty">—</span>
                ) : (
                  value
                )
              }
            />
            <Table.Column
              title={t.columns.mixingTime}
              dataIndex="mixingTime"
              align="right"
              render={(value) =>
                value == null ? (
                  <span className="empty">—</span>
                ) : (
                  `${value} min`
                )
              }
            />
            <Table.Column
              title={t.columns.plannedOriginalMatter}
              dataIndex="planned"
              align="right"
              render={(value) => numberMask(value)}
            />
            <Table.Column
              title={t.columns.realizedOriginalMatter}
              dataIndex="realized"
              align="right"
              render={(value) =>
                value == null ? (
                  <span className="empty">—</span>
                ) : (
                  numberMask(value)
                )
              }
            />
          </Table>
          {isExecuted && executedBatch.observation && (
            <div className="batchObservation">{executedBatch.observation}</div>
          )}
        </BatchCard>,
      );
    }

    return cards;
  };

  return (
    <Container
      title={
        order != null ? (
          <Row type="flex" align="middle" gutter={8}>
            <Col>
              <strong>
                {t.title} {order.code}
              </strong>
            </Col>
            <Col>
              <TagStatus
                {...(STATUS_COLORS[order.status] ?? STATUS_COLORS.Calculated)}
              >
                {translation.productionOrder.status[order.status] ??
                  order.status}
              </TagStatus>
            </Col>
          </Row>
        ) : (
          t.title
        )
      }
      width={840}
      onClose={onClose}
      maskClosable={false}
      visible={visible}
    >
      <Spin spinning={isLoading}>
        <Body>
          {order != null && (
            <>
              <HeaderFields>
                <Row type="flex" gutter={8}>
                  {field(
                    t.date,
                    order.productionDate
                      ? moment(order.productionDate).format("DD/MM/YYYY")
                      : "-",
                  )}
                  {field(
                    t.targetDryMatter,
                    `${numberMask(order.targetDryMatterKg)} kg`,
                  )}
                  {field(t.batches, order.numberOfBatches ?? 0)}
                </Row>
                <Row type="flex" gutter={8}>
                  {field(
                    t.totalCost,
                    numberMask(order.totalCost, true, null, null, 5, 5),
                  )}
                  {field(
                    t.costPerKg,
                    numberMask(
                      order.costPerKgDryMatter,
                      true,
                      null,
                      null,
                      5,
                      2,
                    ),
                  )}
                  {(order.executedBatches ?? 0) > 0 &&
                    field(
                      t.realizedCost,
                      numberMask(
                        order.realizedTotalCost,
                        true,
                        null,
                        null,
                        5,
                        2,
                      ),
                    )}
                </Row>
              </HeaderFields>

              {numberOfBatches === 0 ? (
                <span className="empty">
                  {translation.production.noBatchesYet}
                </span>
              ) : (
                batchCards()
              )}
            </>
          )}
        </Body>
        <Footer>
          <Row type="flex">
            <Col span={24}>
              <div className="buttonsDiv">
                <ButtonStandard
                  type="button"
                  buttonType="type7"
                  onClick={onClose}
                >
                  {translation.buttons.close}
                </ButtonStandard>
                <ButtonStandard
                  type="button"
                  buttonType="type6"
                  onClick={handlePrint}
                >
                  {translation.production.buttonPrint}
                </ButtonStandard>
              </div>
            </Col>
          </Row>
        </Footer>
      </Spin>
    </Container>
  );
};

export default DrawerProductionOrderDetails;
